/**
 * Province GeoJSON loader - HTTP-based dynamic loading
 *
 * Loads province files from external /storage folder via HTTP fetch.
 * Province files are named: {ISO2}_provinces.geo.json (e.g., AF_provinces.geo.json)
 *
 * Storage location:
 * - Development: /storage/provinces/ folder served by Vue dev server
 * - Production: /storage/provinces/ served by your web server (nginx, Apache, backend, etc.)
 *
 * Files are NOT bundled in the application - they're loaded on-demand from external storage.
 */

/**
 * Get the base URL for province GeoJSON files
 * @returns {string} Base URL for storage folder
 */
function getStorageBaseUrl() {
  // Check for dedicated storage URL (optional, for flexibility)
  // This allows you to serve storage from: backend API, CDN, or separate server
  const storageUrl = process.env.VUE_APP_STORAGE_URL;

  if (storageUrl) {
    // Use explicit storage URL if provided
    return `${storageUrl}/storage/provinces`;
  }

  // Default: Use relative path - files served by frontend server (Vue dev server or nginx)
  // In development: Vue dev server serves from /storage
  // In production: nginx/Apache serves from /storage
  return '/storage/provinces';
}

/**
 * Helper function to load provinces for a country
 * Fetches {countryCode}_provinces.geo.json via HTTP
 *
 * @param {string} countryCode - ISO 3166-1 alpha-2 code (2-letter, e.g., "AF")
 * @returns {Promise<Object|null>} GeoJSON FeatureCollection or null if not found
 */
export async function loadProvinces(countryCode) {
  if (!countryCode) {
    console.warn('[loadProvinces] No country code provided');
    return null;
  }

  const lookupCode = countryCode.toUpperCase();
  const baseUrl = getStorageBaseUrl();
  const url = `${baseUrl}/${lookupCode}_provinces.geo.json`;

  try {
    console.log(`[loadProvinces] Fetching province data from: ${url}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log(`[loadProvinces] No province data available for ${lookupCode} (404)`);
        return null;
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const geoJson = await response.json();
    console.log(`[loadProvinces] ✅ Successfully loaded province data for ${lookupCode}`);
    return geoJson;
  } catch (error) {
    // Network error, CORS issue, or file doesn't exist
    console.log(`[loadProvinces] Failed to load province data for ${lookupCode}:`, error.message);
    return null;
  }
}
